import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  approveAgentUpdateRollout,
  cancelAgentUpdateRollout,
  createAgentUpdateRollout,
  getAgentUpdateRollout,
  previewAgentUpdateRollout,
  retryAgentUpdateRolloutNodes,
} from '@/api/releases'
import { listNodes } from '@/api/nodes'
import type {
  AgentReleaseSummary,
  AgentUpdateRolloutDetail,
  AgentUpdateRolloutFilters,
  AgentUpdateRolloutNodeInfo,
  AgentUpdateRolloutNodeStatus,
  AgentUpdateRolloutPreviewResponse,
  NodeSummary,
} from '@/types'

interface Props {
  release: AgentReleaseSummary
  rolloutId?: string
  onClose: () => void
  onToast: (message: string) => void
  onRolloutUpdated?: () => void
}

const EMPTY_FILTERS: AgentUpdateRolloutFilters = {
  company: '',
  site: '',
  status: null,
  current_version: '',
}
const CURRENT_VERSION_NONE = '__none__'
const NODE_FILTER_LIMIT = 1000

export default function AgentRolloutModal({ release, rolloutId, onClose, onToast, onRolloutUpdated }: Props) {
  const [filters, setFilters] = useState<AgentUpdateRolloutFilters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<AgentUpdateRolloutFilters>(EMPTY_FILTERS)
  const [preview, setPreview] = useState<AgentUpdateRolloutPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [manualExcluded, setManualExcluded] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [confirmVersion, setConfirmVersion] = useState('')
  const [canarySize, setCanarySize] = useState(3)
  const [batchSize, setBatchSize] = useState(5)
  const [reason, setReason] = useState('')
  const [creating, setCreating] = useState(false)
  const [rollout, setRollout] = useState<AgentUpdateRolloutDetail | null>(null)
  const [approving, setApproving] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [selectedRetry, setSelectedRetry] = useState<Set<string>>(new Set())
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [nodesLoading, setNodesLoading] = useState(true)
  const [nodesError, setNodesError] = useState<string | null>(null)
  const detailMode = Boolean(rolloutId)

  const companyOptions = useMemo(() => buildCompanyOptions(nodes), [nodes])
  const siteOptions = useMemo(
    () => buildSiteOptions(nodes, filters.company ?? ''),
    [nodes, filters.company],
  )
  const selectedScopeCount = useMemo(
    () => countNodesForScope(nodes, filters.company ?? ''),
    [nodes, filters.company],
  )
  const currentVersionOptions = useMemo(
    () => buildCurrentVersionOptions(nodes, filters),
    [nodes, filters.company, filters.site, filters.status],
  )
  const currentVersionScopeCount = useMemo(
    () => countNodesForVersionScope(nodes, filters),
    [nodes, filters.company, filters.site, filters.status],
  )

  const loadPreview = useCallback(async (
    nextFilters: AgentUpdateRolloutFilters,
    nextManualExcluded: Set<string>,
  ) => {
    setPreviewLoading(true)
    setError(null)
    try {
      const res = await previewAgentUpdateRollout(
        release.version,
        cleanFilters(nextFilters),
        Array.from(nextManualExcluded),
      )
      setPreview(res)
      setAppliedFilters(cleanFilters(nextFilters))
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? '배포 대상 preview를 불러올 수 없습니다.')
    } finally {
      setPreviewLoading(false)
    }
  }, [release.version])

  useEffect(() => {
    if (!detailMode) {
      loadPreview(EMPTY_FILTERS, new Set())
    }
  }, [detailMode, loadPreview])

  useEffect(() => {
    if (!rolloutId) return
    const activeRolloutId = rolloutId
    let mounted = true

    async function loadRolloutDetail() {
      setError(null)
      try {
        const detail = await getAgentUpdateRollout(activeRolloutId)
        if (!mounted) return
        setRollout(detail)
        onRolloutUpdated?.()
      } catch (e: unknown) {
        if (!mounted) return
        const err = e as { message?: string }
        setError(err?.message ?? 'Rollout 상세를 불러올 수 없습니다.')
      } finally {
        if (mounted) setPreviewLoading(false)
      }
    }

    setPreviewLoading(true)
    loadRolloutDetail()
    return () => {
      mounted = false
    }
  }, [rolloutId, onRolloutUpdated])

  useEffect(() => {
    let mounted = true

    async function loadNodesForFilters() {
      setNodesLoading(true)
      setNodesError(null)
      try {
        const res = await listNodes({ limit: NODE_FILTER_LIMIT })
        if (!mounted) return
        setNodes(res.items)
      } catch {
        if (!mounted) return
        setNodesError('고객사/구역 목록을 불러올 수 없습니다.')
      } finally {
        if (mounted) setNodesLoading(false)
      }
    }

    loadNodesForFilters()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!rollout || ['completed', 'failed', 'cancelled'].includes(rollout.status)) return
    const id = window.setInterval(async () => {
      try {
        const next = await getAgentUpdateRollout(rollout.id)
        setRollout(next)
        onRolloutUpdated?.()
      } catch {
        // Keep the last known state visible.
      }
    }, 5000)
    return () => window.clearInterval(id)
  }, [rollout, onRolloutUpdated])

  async function startRollout() {
    if (!preview) return
    setCreating(true)
    setError(null)
    try {
      const created = await createAgentUpdateRollout(release.version, {
        confirm_version: confirmVersion.trim(),
        filters: appliedFilters,
        exclude_node_ids: Array.from(manualExcluded),
        canary_size: canarySize,
        batch_size: batchSize,
        reason: reason.trim() || null,
      })
      setRollout(created)
      onRolloutUpdated?.()
      onToast(`Rollout 시작: ${release.version}`)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Rollout 시작 실패')
    } finally {
      setCreating(false)
    }
  }

  async function approve() {
    if (!rollout) return
    setApproving(true)
    setError(null)
    try {
      const next = await approveAgentUpdateRollout(rollout.id)
      setRollout(next)
      onRolloutUpdated?.()
      onToast('Canary 성공 확인 후 본 배포를 시작했습니다.')
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? '본 배포 승인 실패')
    } finally {
      setApproving(false)
    }
  }

  async function retrySelected() {
    if (!rollout || selectedRetry.size === 0) return
    setRetrying(true)
    setError(null)
    try {
      const next = await retryAgentUpdateRolloutNodes(rollout.id, Array.from(selectedRetry))
      setRollout(next)
      setSelectedRetry(new Set())
      onRolloutUpdated?.()
      onToast('선택한 실패 노드를 재시도했습니다.')
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? '재시도 실패')
    } finally {
      setRetrying(false)
    }
  }

  async function cancelRollout() {
    if (!rollout || cancelling) return
    if (!window.confirm('진행 중인 agent rollout을 중단할까요?')) return
    setCancelling(true)
    setError(null)
    try {
      const next = await cancelAgentUpdateRollout(rollout.id)
      setRollout(next)
      onRolloutUpdated?.()
      onToast('Rollout을 중단했습니다.')
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Rollout 중단 실패')
    } finally {
      setCancelling(false)
    }
  }

  function toggleManualExclude(nodeId: string) {
    const next = new Set(manualExcluded)
    if (next.has(nodeId)) next.delete(nodeId)
    else next.add(nodeId)
    setManualExcluded(next)
    loadPreview(appliedFilters, next)
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Agent rollout</h3>
            <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
              대상 버전 <code style={codeStyle}>{release.version}</code>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              ...secondaryBtnStyle,
            }}
          >
            닫기
          </button>
        </div>

        {error && <div style={errorBoxStyle}>{error}</div>}

        {rollout ? (
          <RolloutProgress
            rollout={rollout}
            approving={approving}
            retrying={retrying}
            cancelling={cancelling}
            selectedRetry={selectedRetry}
            setSelectedRetry={setSelectedRetry}
            onApprove={approve}
            onRetry={retrySelected}
            onCancel={cancelRollout}
          />
        ) : detailMode ? (
          <section style={sectionStyle}>
            <div style={mutedStyle}>
              {previewLoading ? 'Rollout 상세를 불러오는 중…' : 'Rollout 상세가 없습니다.'}
            </div>
          </section>
        ) : (
          <>
            <section style={sectionStyle}>
              <div style={sectionTitleStyle}>대상 필터</div>
              {nodesError && <div style={{ ...errorBoxStyle, marginTop: 10, marginBottom: 0 }}>{nodesError}</div>}
              <div style={filterGridStyle}>
                <label style={labelStyle}>
                  고객사
                  <select
                    value={filters.company ?? ''}
                    onChange={(e) => setFilters({ ...filters, company: e.target.value, site: '' })}
                    disabled={nodesLoading || Boolean(nodesError)}
                    style={inputStyle}
                  >
                    <option value="">
                      {nodesLoading ? '불러오는 중...' : `전체 고객사 (${nodes.length}대)`}
                    </option>
                    {companyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  구역
                  <select
                    value={filters.site ?? ''}
                    onChange={(e) => setFilters({ ...filters, site: e.target.value })}
                    disabled={nodesLoading || Boolean(nodesError)}
                    style={inputStyle}
                  >
                    <option value="">
                      {nodesLoading ? '불러오는 중...' : `전체 구역 (${selectedScopeCount}대)`}
                    </option>
                    {siteOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={labelStyle}>
                  Status
                  <select
                    value={filters.status ?? ''}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value as AgentUpdateRolloutFilters['status'] || null })}
                    style={inputStyle}
                  >
                    <option value="">All</option>
                    <option value="online">online</option>
                    <option value="warning">warning</option>
                    <option value="offline">offline</option>
                  </select>
                </label>
                <label style={labelStyle}>
                  현재 설치 버전
                  <select
                    value={filters.current_version ?? ''}
                    onChange={(e) => setFilters({ ...filters, current_version: e.target.value })}
                    disabled={nodesLoading || Boolean(nodesError)}
                    style={inputStyle}
                  >
                    <option value="">
                      {nodesLoading ? '불러오는 중...' : `전체 버전 (${currentVersionScopeCount}대)`}
                    </option>
                    {currentVersionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button onClick={() => loadPreview(filters, manualExcluded)} disabled={previewLoading} style={secondaryBtnStyle}>
                  {previewLoading ? '계산 중…' : '필터 적용'}
                </button>
              </div>
            </section>

            <section style={sectionStyle}>
              <div style={sectionTitleStyle}>Preview</div>
              {previewLoading ? (
                <div style={mutedStyle}>대상 계산 중…</div>
              ) : preview ? (
                <PreviewPane
                  preview={preview}
                  manualExcluded={manualExcluded}
                  onToggleManualExclude={toggleManualExclude}
                />
              ) : null}
            </section>

            <section style={sectionStyle}>
              <div style={sectionTitleStyle}>Rollout 설정</div>
              <div style={settingsGridStyle}>
                <label style={labelStyle}>
                  Canary
                  <select value={canarySize} onChange={(e) => setCanarySize(Number(e.target.value))} style={inputStyle}>
                    <option value={1}>1대</option>
                    <option value={3}>3대</option>
                    <option value={5}>5대</option>
                  </select>
                </label>
                <label style={labelStyle}>
                  Batch
                  <select value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value))} style={inputStyle}>
                    <option value={3}>3대</option>
                    <option value={5}>5대</option>
                    <option value={10}>10대</option>
                  </select>
                </label>
                <label style={labelStyle}>
                  확인 버전
                  <input
                    value={confirmVersion}
                    onChange={(e) => setConfirmVersion(e.target.value)}
                    placeholder={release.version}
                    style={inputStyle}
                  />
                </label>
              </div>
              <label style={{ ...labelStyle, marginTop: 10 }}>
                사유
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="예: 운영 노드 agent hotfix 배포"
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button
                  onClick={startRollout}
                  disabled={
                    creating ||
                    previewLoading ||
                    !preview ||
                    preview.target_count === 0 ||
                    confirmVersion.trim() !== release.version
                  }
                  style={{
                    ...primaryBtnStyle,
                    background:
                      creating || confirmVersion.trim() !== release.version || preview?.target_count === 0
                        ? '#999'
                        : primaryBtnStyle.background,
                  }}
                >
                  {creating ? '시작 중…' : 'Canary 시작'}
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

function PreviewPane({
  preview,
  manualExcluded,
  onToggleManualExclude,
}: {
  preview: AgentUpdateRolloutPreviewResponse
  manualExcluded: Set<string>
  onToggleManualExclude: (nodeId: string) => void
}) {
  return (
    <div>
      <div style={statsGridStyle}>
        <Stat label="대상" value={preview.target_count} tone="#e8f5e9" />
        <Stat label="제외" value={preview.excluded_count} tone="#fff8e1" />
        <Stat label="이미 같은 버전" value={preview.excluded_reason_counts.same_version ?? 0} tone="#f5f5f7" />
        <Stat label="작업 중" value={preview.excluded_reason_counts.active_work ?? 0} tone="#ffebee" />
      </div>
      <NodeList
        title="대상 노드"
        nodes={preview.targets.slice(0, 80)}
        selectable
        selected={manualExcluded}
        onToggle={onToggleManualExclude}
      />
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
          제외 노드 {preview.excluded_count}대
        </summary>
        <NodeList
          title=""
          nodes={preview.excluded.slice(0, 120)}
          showReason
          selectable
          selected={manualExcluded}
          onToggle={onToggleManualExclude}
        />
      </details>
    </div>
  )
}

function RolloutProgress({
  rollout,
  approving,
  retrying,
  cancelling,
  selectedRetry,
  setSelectedRetry,
  onApprove,
  onRetry,
  onCancel,
}: {
  rollout: AgentUpdateRolloutDetail
  approving: boolean
  retrying: boolean
  cancelling: boolean
  selectedRetry: Set<string>
  setSelectedRetry: (next: Set<string>) => void
  onApprove: () => void
  onRetry: () => void
  onCancel: () => void
}) {
  const active = rollout.nodes.filter((n) => n.status === 'running' || n.status === 'queued')
  const failed = rollout.nodes.filter((n) => ['failed', 'cancelled', 'timed_out'].includes(n.status))
  const rolloutNodes = rollout.nodes.filter((n) => n.phase !== 'excluded')
  const completed = rolloutNodes.filter((n) => n.status === 'succeeded').length
  const percent = rollout.target_count === 0 ? 0 : Math.round((completed / rollout.target_count) * 100)
  const batches = useMemo(() => groupBatches(rollout.nodes), [rollout.nodes])
  const activeRollout = !isRolloutFinal(rollout.status)

  return (
    <div>
      <section style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div>
            <div style={sectionTitleStyle}>진행 상태</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {rolloutStatusLabel(rollout.status)}
              {rollout.error_message ? ` · ${rollout.error_message}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {rollout.status === 'awaiting_approval' && (
              <button onClick={onApprove} disabled={approving || cancelling} style={primaryBtnStyle}>
                {approving ? '시작 중…' : '본 배포 시작'}
              </button>
            )}
            {activeRollout && (
              <button
                onClick={onCancel}
                disabled={cancelling || approving}
                style={{
                  ...dangerBtnStyle,
                  cursor: cancelling || approving ? 'not-allowed' : 'pointer',
                }}
              >
                {cancelling ? '중단 중…' : '배포 중단'}
              </button>
            )}
          </div>
        </div>
        <div style={progressTrackStyle}>
          <div style={{ ...progressFillStyle, width: `${percent}%` }} />
        </div>
        <div style={statsGridStyle}>
          <Stat label="업데이트 완료" value={completed} tone="#e8f5e9" />
          <Stat label="대상" value={rollout.target_count} tone="#e3f2fd" />
          <Stat label="진행 중" value={active.length} tone="#fff8e1" />
          <Stat label="실패" value={failed.length} tone="#ffebee" />
        </div>
      </section>

      <section style={sectionStyle}>
        <div style={sectionTitleStyle}>현재 설치 중</div>
        {active.length === 0 ? (
          <div style={mutedStyle}>현재 설치 중인 노드가 없습니다.</div>
        ) : (
          <StatusTable nodes={active} />
        )}
      </section>

      <section style={sectionStyle}>
        <div style={sectionTitleStyle}>Batch 결과</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {batches.map((batch) => (
            <div key={batch.key} style={batchRowStyle}>
              <strong>{batch.label}</strong>
              <span>완료 {batch.succeeded}</span>
              <span>실패 {batch.failed}</span>
              <span>진행 {batch.active}</span>
              <span>대기 {batch.pending}</span>
            </div>
          ))}
        </div>
      </section>

      {failed.length > 0 && (
        <section style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div style={sectionTitleStyle}>실패 노드</div>
            <button
              onClick={onRetry}
              disabled={retrying || selectedRetry.size === 0}
              style={{
                ...secondaryBtnStyle,
                cursor: retrying || selectedRetry.size === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {retrying ? '재시도 중…' : `선택 재시도 (${selectedRetry.size})`}
            </button>
          </div>
          <StatusTable
            nodes={failed}
            selectable
            selected={selectedRetry}
            onToggle={(nodeId) => {
              const next = new Set(selectedRetry)
              if (next.has(nodeId)) next.delete(nodeId)
              else next.add(nodeId)
              setSelectedRetry(next)
            }}
          />
        </section>
      )}

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
          제외 노드 {rollout.excluded_count}대
        </summary>
        <StatusTable nodes={rollout.nodes.filter((n) => n.phase === 'excluded')} />
      </details>
    </div>
  )
}

function NodeList({
  title,
  nodes,
  showReason = false,
  selectable = false,
  selected,
  onToggle,
}: {
  title: string
  nodes: AgentUpdateRolloutNodeInfo[]
  showReason?: boolean
  selectable?: boolean
  selected?: Set<string>
  onToggle?: (nodeId: string) => void
}) {
  if (nodes.length === 0) return <div style={mutedStyle}>{title ? `${title}: ` : ''}없음</div>
  return (
    <div style={{ marginTop: title ? 10 : 8 }}>
      {title && <div style={{ ...sectionTitleStyle, marginBottom: 6 }}>{title}</div>}
      <div style={compactTableWrapStyle}>
        <table style={compactTableStyle}>
          <tbody>
            {nodes.map((node) => (
              <tr key={node.node_id}>
                {selectable && (
                  <td style={{ ...compactTdStyle, width: 28 }}>
                    {(!showReason || node.exclude_reason === 'manual_excluded') && (
                      <input
                        type="checkbox"
                        checked={selected?.has(node.node_id) ?? false}
                        onChange={() => onToggle?.(node.node_id)}
                      />
                    )}
                  </td>
                )}
                <td style={compactTdStyle}>{node.name || node.node_key}</td>
                <td style={compactTdStyle}>{node.company ?? '-'}</td>
                <td style={compactTdStyle}>{node.site ?? '-'}</td>
                <td style={compactTdStyle}>{node.agent_version ?? '-'}</td>
                {showReason && <td style={compactTdStyle}>{excludeReasonLabel(node.exclude_reason)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function StatusTable({
  nodes,
  selectable = false,
  selected,
  onToggle,
}: {
  nodes: AgentUpdateRolloutNodeStatus[]
  selectable?: boolean
  selected?: Set<string>
  onToggle?: (nodeId: string) => void
}) {
  if (nodes.length === 0) return <div style={mutedStyle}>없음</div>
  return (
    <div style={compactTableWrapStyle}>
      <table style={compactTableStyle}>
        <tbody>
          {nodes.map((node) => (
            <tr key={node.id}>
              {selectable && (
                <td style={{ ...compactTdStyle, width: 28 }}>
                  <input
                    type="checkbox"
                    checked={selected?.has(node.node_id) ?? false}
                    onChange={() => onToggle?.(node.node_id)}
                  />
                </td>
              )}
              <td style={compactTdStyle}>{node.name || node.node_key}</td>
              <td style={compactTdStyle}>{node.company ?? '-'}</td>
              <td style={compactTdStyle}>{node.site ?? '-'}</td>
              <td style={compactTdStyle}>{node.agent_version ?? '-'}</td>
              <td style={compactTdStyle}>{nodeStatusLabel(node.status)}</td>
              <td style={compactTdStyle}>{node.error_message || excludeReasonLabel(node.exclude_reason)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div style={{ ...statBoxStyle, background: tone }}>
      <div style={{ fontSize: 11, color: '#666' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    </div>
  )
}

function cleanFilters(filters: AgentUpdateRolloutFilters): AgentUpdateRolloutFilters {
  return {
    company: filters.company?.trim() || null,
    site: filters.site?.trim() || null,
    status: filters.status || null,
    current_version: filters.current_version?.trim() || null,
  }
}

function buildCompanyOptions(nodes: NodeSummary[]) {
  const counts = new Map<string, number>()
  for (const node of nodes) {
    const company = node.company?.trim()
    if (!company) continue
    counts.set(company, (counts.get(company) ?? 0) + 1)
  }

  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({
      value,
      label: `${value} (${count}대)`,
    }))
}

function buildSiteOptions(nodes: NodeSummary[], company: string) {
  const counts = new Map<string, number>()
  for (const node of nodes) {
    if (company && node.company !== company) continue
    const site = node.site?.trim()
    if (!site) continue
    counts.set(site, (counts.get(site) ?? 0) + 1)
  }

  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({
      value,
      label: `${value} (${count}대)`,
    }))
}

function countNodesForScope(nodes: NodeSummary[], company: string): number {
  if (!company) return nodes.length
  return nodes.filter((node) => node.company === company).length
}

function buildCurrentVersionOptions(nodes: NodeSummary[], filters: AgentUpdateRolloutFilters) {
  const counts = new Map<string, number>()
  let missingCount = 0

  for (const node of nodes) {
    if (!matchesLocationAndStatus(node, filters)) continue
    const version = node.agent_version?.trim()
    if (!version) {
      missingCount += 1
      continue
    }
    counts.set(version, (counts.get(version) ?? 0) + 1)
  }

  const options = Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([value, count]) => ({
      value,
      label: `${value} (${count}대)`,
    }))

  if (missingCount > 0) {
    options.push({
      value: CURRENT_VERSION_NONE,
      label: `버전 없음 (${missingCount}대)`,
    })
  }

  return options
}

function countNodesForVersionScope(nodes: NodeSummary[], filters: AgentUpdateRolloutFilters): number {
  return nodes.filter((node) => matchesLocationAndStatus(node, filters)).length
}

function matchesLocationAndStatus(node: NodeSummary, filters: AgentUpdateRolloutFilters): boolean {
  if (filters.company && node.company !== filters.company) return false
  if (filters.site && node.site !== filters.site) return false
  if (filters.status && node.status !== filters.status) return false
  return true
}

function groupBatches(nodes: AgentUpdateRolloutNodeStatus[]) {
  const grouped = new Map<string, AgentUpdateRolloutNodeStatus[]>()
  for (const node of nodes.filter((n) => n.phase !== 'excluded')) {
    const key = node.phase === 'canary' ? 'canary' : `batch-${node.batch_index ?? 'pending'}`
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(node)
  }
  return Array.from(grouped.entries()).map(([key, rows]) => ({
    key,
    label: key === 'canary' ? 'Canary' : key.replace('batch-', 'Batch '),
    succeeded: rows.filter((r) => r.status === 'succeeded').length,
    failed: rows.filter((r) => ['failed', 'cancelled', 'timed_out'].includes(r.status)).length,
    active: rows.filter((r) => ['queued', 'running'].includes(r.status)).length,
    pending: rows.filter((r) => r.status === 'pending').length,
  }))
}

function rolloutStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    running_canary: 'Canary 진행 중',
    awaiting_approval: 'Canary 완료',
    running: '본 배포 진행 중',
    completed: '배포 완료',
    failed: '중단됨',
    cancelled: '취소됨',
  }
  return labels[status] ?? status
}

function isRolloutFinal(status: string): boolean {
  return ['completed', 'failed', 'cancelled'].includes(status)
}

function nodeStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: '대기',
    queued: '대기 중',
    running: '설치 중',
    succeeded: '업데이트 완료',
    failed: '실패',
    timed_out: '시간 초과',
    cancelled: '취소',
    excluded: '제외',
  }
  return labels[status] ?? status
}

function excludeReasonLabel(reason?: string | null): string {
  const labels: Record<string, string> = {
    same_version: '이미 같은 버전',
    offline: '오프라인',
    active_work: '녹화/업로드 중',
    agent_update_active: '업데이트 진행 중',
    downgrade: '다운그레이드',
    manual_excluded: '수동 제외',
  }
  return reason ? labels[reason] ?? reason : ''
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.48)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 120,
}

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: 22,
  width: 'min(980px, 94vw)',
  maxHeight: '92vh',
  overflow: 'auto',
  boxShadow: '0 18px 50px rgba(0,0,0,0.24)',
}

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: 14,
}

const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid #eee',
  paddingTop: 14,
  marginTop: 14,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
}

const filterGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(130px, 1fr))',
  gap: 10,
  marginTop: 10,
}

const settingsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '140px 140px 1fr',
  gap: 10,
  marginTop: 10,
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#555',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '7px 8px',
  marginTop: 4,
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '7px 14px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 12px',
  background: '#f5f5f7',
  color: '#333',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 12px',
  background: '#c62828',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
  gap: 8,
  marginTop: 10,
}

const statBoxStyle: React.CSSProperties = {
  border: '1px solid rgba(0,0,0,0.06)',
  borderRadius: 6,
  padding: '9px 10px',
}

const compactTableWrapStyle: React.CSSProperties = {
  maxHeight: 220,
  overflow: 'auto',
  border: '1px solid #eee',
  borderRadius: 6,
}

const compactTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
}

const compactTdStyle: React.CSSProperties = {
  padding: '7px 9px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
}

const mutedStyle: React.CSSProperties = {
  padding: 10,
  color: '#888',
  fontSize: 13,
}

const errorBoxStyle: React.CSSProperties = {
  padding: 10,
  background: '#fdecea',
  color: '#c62828',
  borderRadius: 4,
  fontSize: 12,
  marginBottom: 12,
}

const codeStyle: React.CSSProperties = {
  background: '#f5f5f7',
  padding: '1px 6px',
  borderRadius: 3,
}

const progressTrackStyle: React.CSSProperties = {
  height: 8,
  borderRadius: 4,
  background: '#eee',
  overflow: 'hidden',
  marginTop: 12,
}

const progressFillStyle: React.CSSProperties = {
  height: '100%',
  background: '#2e7d32',
  transition: 'width 0.2s',
}

const batchRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr repeat(4, 80px)',
  gap: 8,
  padding: '8px 10px',
  border: '1px solid #eee',
  borderRadius: 6,
  fontSize: 12,
  alignItems: 'center',
}
